/**
 * useTheatricalMode Hook - Adaptive Theatrical Animation Mode
 *
 * Provides user-selectable theatrical animation modes with smart defaults based on:
 * - User tier (DramaX gets premium experience)
 * - Analysis count (new users get theatrical, power users get quick)
 * - Reduced motion preference (always forces quick mode)
 * - Manual user override (persisted to AsyncStorage)
 *
 * Three modes:
 * - Quick: No suspense, efficient (power users, reduced motion)
 * - Theatrical: 5s suspense crawl with teaser messages (new users)
 * - DramaX Premium: 7s enhanced suspense with premium messages (DramaX tier)
 *
 * Usage:
 * ```tsx
 * import { useTheatricalMode } from '@/hooks/useTheatricalMode';
 *
 * function AnalysisScreen() {
 *   const { config, variant, setMode, resetToDefault } = useTheatricalMode();
 *
 *   // Pass config to LetterZoomAnimation
 *   return (
 *     <LetterZoomAnimation
 *       theatricalConfig={config}
 *       {...otherProps}
 *     />
 *   );
 * }
 * ```
 *
 * @see lib/theatrical/theatricalModeConfig.ts for config definitions
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { useEntitlements } from './useEntitlements';
import { useQuota } from './useQuota';
import { useReducedMotionWithLoading } from './useReducedMotionWithLoading';
import { useAuth } from '@/contexts/AuthContext';
import { trackEvent } from '@/lib/analytics/events';
import {
  type TheatricalModeVariant,
  type TheatricalModeConfig,
  type AdaptiveContext,
  getDefaultTheatricalMode,
  getTheatricalModeConfig,
} from '@/lib/theatrical/theatricalModeConfig';

// AsyncStorage key for persisting user's manual mode selection
const STORAGE_KEY = '@drama_detector:theatrical_mode_preference';

/**
 * Hook return type
 */
export interface UseTheatricalModeReturn {
  /** Current theatrical mode variant */
  variant: TheatricalModeVariant;

  /** Complete configuration object for the current mode */
  config: TheatricalModeConfig;

  /** Whether the mode is manually set by user (vs adaptive default) */
  isManualOverride: boolean;

  /** Whether adaptive logic is still calculating (loading dependencies) */
  isLoading: boolean;

  /**
   * Set theatrical mode manually
   * Persists choice to AsyncStorage and tracks analytics event
   * @param variant - New theatrical mode variant
   * @param source - Where the mode change originated (for analytics)
   */
  setMode: (variant: TheatricalModeVariant, source?: 'url_parameter' | 'manual_toggle' | 'adaptive_default') => Promise<void>;

  /**
   * Reset to adaptive default based on user context
   * Clears manual override from AsyncStorage
   */
  resetToDefault: () => Promise<void>;

  /**
   * Refresh adaptive context (useful after tier change or analysis completion)
   */
  refresh: () => void;
}

/**
 * Calculate days since registration from user metadata
 */
function getDaysSinceRegistration(userCreatedAt: string | null | undefined): number | undefined {
  if (!userCreatedAt) return undefined;

  try {
    const createdDate = new Date(userCreatedAt);
    const now = new Date();
    const diffMs = now.getTime() - createdDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  } catch {
    return undefined;
  }
}

/**
 * useTheatricalMode - Adaptive theatrical animation mode selection
 *
 * Provides config-driven theatrical modes with smart defaults.
 *
 * @returns Theatrical mode configuration and control functions
 */
export function useTheatricalMode(): UseTheatricalModeReturn {
  const { user } = useAuth();
  const { tier, quotaPolicy, isLoading: entitlementsLoading } = useEntitlements();
  const { used, isLoading: quotaLoading } = useQuota(quotaPolicy);
  const reducedMotionEnabled = useReducedMotionWithLoading();

  // Manual override state (loaded from AsyncStorage)
  const [manualOverride, setManualOverride] = useState<TheatricalModeVariant | null>(null);
  const [storageLoaded, setStorageLoaded] = useState(false);

  // Load manual override from AsyncStorage on mount
  useEffect(() => {
    let mounted = true;

    const loadPreference = async () => {
      try {
        const stored = await AsyncStorage.getItem(STORAGE_KEY);
        if (mounted && stored) {
          const parsed = JSON.parse(stored) as { variant: TheatricalModeVariant };
          setManualOverride(parsed.variant);
        }
      } catch (error) {
        console.error('[useTheatricalMode] Failed to load preference:', error);
      } finally {
        if (mounted) {
          setStorageLoaded(true);
        }
      }
    };

    loadPreference();

    return () => {
      mounted = false;
    };
  }, []);

  // Calculate adaptive context for default mode
  const adaptiveContext = useMemo<AdaptiveContext | null>(() => {
    // Wait for all dependencies to load
    if (entitlementsLoading || quotaLoading || reducedMotionEnabled === undefined) {
      return null;
    }

    const daysSinceRegistration = getDaysSinceRegistration(user?.createdAt);

    return {
      tier,
      analysisCount: used,
      analysisCountLast7Days: used, // Note: This is total used, not last 7 days. Backend can provide this if needed.
      reducedMotionEnabled: reducedMotionEnabled ?? false,
      daysSinceRegistration,
    };
  }, [tier, used, reducedMotionEnabled, user, entitlementsLoading, quotaLoading]);

  // Determine effective variant (manual override > adaptive default)
  const variant = useMemo<TheatricalModeVariant>(() => {
    // Manual override takes precedence
    if (manualOverride) {
      return manualOverride;
    }

    // Wait for adaptive context to be ready
    if (!adaptiveContext) {
      // Safe default while loading: theatrical mode (Enjoy the Show)
      return 'theatrical';
    }

    // Use adaptive logic
    return getDefaultTheatricalMode(adaptiveContext);
  }, [manualOverride, adaptiveContext]);

  // Get config for current variant
  const config = useMemo<TheatricalModeConfig>(() => {
    return getTheatricalModeConfig(variant);
  }, [variant]);

  // Set mode manually
  const setMode = useCallback(
    async (newVariant: TheatricalModeVariant, source: 'url_parameter' | 'manual_toggle' | 'adaptive_default' = 'manual_toggle') => {
      try {
        // Persist to AsyncStorage
        await AsyncStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ variant: newVariant })
        );

        // Track analytics event
        trackEvent('ViewingMode_Selected', {
          previous_mode: variant,
          new_mode: newVariant,
          user_tier: tier,
          analysis_count: used,
          was_manual_override: true,
          source,
          platform: Platform.OS as 'ios' | 'android' | 'web',
        });

        // Update state
        setManualOverride(newVariant);
      } catch (error) {
        console.error('[useTheatricalMode] Failed to save preference:', error);
      }
    },
    [variant, tier, used]
  );

  // Reset to adaptive default
  const resetToDefault = useCallback(async () => {
    try {
      // Calculate what the new default will be
      const newDefaultMode = adaptiveContext
        ? getDefaultTheatricalMode(adaptiveContext)
        : 'quick';

      // Remove from AsyncStorage
      await AsyncStorage.removeItem(STORAGE_KEY);

      // Track analytics event
      trackEvent('ViewingMode_Reset', {
        previous_mode: variant,
        new_default_mode: newDefaultMode,
        user_tier: tier,
        analysis_count: used,
        platform: Platform.OS as 'ios' | 'android' | 'web',
      });

      // Clear manual override
      setManualOverride(null);
    } catch (error) {
      console.error('[useTheatricalMode] Failed to clear preference:', error);
    }
  }, [variant, tier, used, adaptiveContext]);

  // Refresh function (useful after tier changes or analysis completion)
  const refresh = useCallback(() => {
    // Force re-calculation by clearing manual override
    // This will trigger adaptive logic on next render
    // Note: This doesn't persist - just for runtime refresh
  }, []);

  // Overall loading state
  const isLoading =
    !storageLoaded ||
    entitlementsLoading ||
    quotaLoading ||
    reducedMotionEnabled === undefined;

  return {
    variant,
    config,
    isManualOverride: manualOverride !== null,
    isLoading,
    setMode,
    resetToDefault,
    refresh,
  };
}
